/**
 * A modal allowing users to select a source and target language for translation of the work.
 * Premium users receive the translatfed document.
 * Free users are upsold the translation product.
 */
import React, { Component } from 'react';
import styled from 'astroturf/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons';

import ModalBase from 'js/components/modals/Modal';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import $image_url from 'js/utils/image_url';
import { recordShow, recordDismissal } from 'js/utils/dismissibles';

const Modal = styled(ModalBase)`
  display: flex;
  flex-direction: column;
  border-radius: 0;
  background-color: #ffffff;
  box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.5);
  padding: 0;
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  max-width: 1350px;
  height: 700px;
  max-height: 700px;
`;

const X = styled(FontAwesomeIcon)`
  position: absolute;
  right: 24px;
  top: 20px;
  color: #7a7a7a;
  font-size: 20px;
  cursor: pointer;
`;

const Body = styled.div`
  display: flex;
  margin: 95px 85px 170px 85px;
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 44px;
  line-height: 56px;
  letter-spacing: -0.015em;
  color: #222222;
  margin: 0;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 12px;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 580px;
  margin-right: 80px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: flex-start;
  width: 520px;
  position: relative;
`;

const BulletLine = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const SubText = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 24px;
  color: #222233;
`;

const Button = styled.a`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 47px;

  width: 368px;

  background: #0849b8;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.2);
  border-radius: 4px;

  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;

  text-align: center;

  color: #ffffff;

  &:hover:hover {
    background-color: #367bef;
    color: #ffffff;
    text-decoration: none;
  }
`;

const BigQuotes = styled.img`
  position: absolute;
  top: 65px;
  left: -10px;
`;

const TestimonialContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 120px;
  left: 90px;
`;

const TestimonialText = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 22px;
  line-height: 28px;

  width: 370px;
  height: 150px;

  margin-bottom: 20px;
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  width: 370px;
`;

const ProfilePicture = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  margin-right: 18px;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileName = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  margin-bottom: 4px;
`;

const ProfileAffiliation = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 20px;
  color: #6b6b82;
`;

interface Props {
  onClose: () => void;
  workId: number;
}

class LettersModal extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  componentDidMount() {
    recordShow('LettersInformationModal');
  }

  onClose = (): void => {
    recordDismissal('LettersInformationModal');
    this.props.onClose();
  };

  render(): React.ReactElement {
    const { workId } = this.props;

    return (
      <Modal onClose={this.onClose}>
        <ImpressionWatcher
          source="letters_info_modal"
          entity_type={2}
          entity_id={workId}
        >
          <Content>
            <X
              icon={faTimes}
              onClick={this.onClose}
              data-kale-component="closeButton"
              data-kale-collection="letters_info_modal"
            />
            <Body>
              <LeftSection>
                <Header>
                  <Title>
                    Get your work published quickly with Academia Letters
                  </Title>
                </Header>
                <BulletLine>
                  <Icon src={$image_url('letters/blue_hourglass.svg')} />
                  <SubText>Rapid Publishing</SubText>
                </BulletLine>
                <BulletLine>
                  <Icon src={$image_url('letters/blue_profile.svg')} />
                  <SubText>Comprehensive Peer Reviews</SubText>
                </BulletLine>
                <BulletLine>
                  <Icon src={$image_url('letters/blue_world.svg')} />
                  <SubText>Wide Dissemination</SubText>
                </BulletLine>
                <SubText
                  style={{
                    marginTop: '12px',
                    marginBottom: '40px',
                    width: '400px',
                  }}
                >
                  Academia Letters aims to rapidly publish short-form articles
                  such as brief reports, case studies, "orphaned" findings, and
                  ideas dropped from previously-published work.
                </SubText>
                <Button
                  href={'/letters/about?trigger=letter-information-modal'}
                  data-kale-component="ctaButton"
                  data-kale-collection="letters_info_modal"
                >
                  Learn more about Academia Letters&nbsp;
                  <FontAwesomeIcon icon={faCaretRight} />
                </Button>
              </LeftSection>
              <RightSection
                style={{
                  backgroundImage: `url(${$image_url(
                    'letters/blue_blob_background.svg',
                  )})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                }}
              >
                <BigQuotes src={$image_url('letters/blue_quotes.svg')} />
                <TestimonialContainer>
                  <TestimonialText>
                    Publishing with Academia Letters was great. I had this short
                    essay and I couldn't set the time aside to work on it. This
                    is a great opportunity--it will go out there and people will
                    read it.
                  </TestimonialText>
                  <Profile>
                    <ProfilePicture
                      src={$image_url('people/elana_gomel.png')}
                    />
                    <ProfileInfo>
                      <ProfileName>Elana Gomel</ProfileName>
                      <ProfileAffiliation>
                        Tel Aviv University
                      </ProfileAffiliation>
                    </ProfileInfo>
                  </Profile>
                </TestimonialContainer>
              </RightSection>
            </Body>
          </Content>
        </ImpressionWatcher>
      </Modal>
    );
  }
}

export default LettersModal;
